import React, { useMemo, useRef, useState } from "react";
import addNotification from "helpers/notify";
import { useDatabaseWorkspace, useScopedList } from "hooks/useEstimatingDatabase";
import { DatabasePageShell } from "components/EstimatingDatabase/DatabasePageShell";
import { RecordModal } from "components/EstimatingDatabase/RecordModal";
import { ImportModeModal, ChoiceModal, confirmDialog } from "components/EstimatingDatabase/DatabaseModals";
import { formatMoney, formatNumber } from "components/EstimatingDatabase/DatabaseTable";
import { useLookup, awpOptions } from "components/EstimatingDatabase/lookups";
import {
  matchesSearch,
  nextTopCode,
  nextChildCode,
  attachChildren,
  compareCodeText,
  cloneParentWithChildren,
  cloneChild,
  SCOPE_BIDLIGHT,
} from "helpers/estimatingDatabase/databaseRows";
import {
  YIELD_FIELDS,
  isMetricTemplate,
  yieldToDisplay,
  yieldFromDisplay,
  yieldUnitLabel,
  yieldForUnit,
  crewHourlyCost,
  crewCostPerUnit,
  crewTotalPercentage,
} from "helpers/estimatingDatabase/databaseUnits";
import {
  readWorkbook,
  downloadWorkbook,
  buildLaborWorkbook,
  readLaborWorkbook,
} from "helpers/estimatingDatabase/databaseXlsx";
import { CREW_FIELDS, LABOR_FIELDS } from "constants/estimatingDatabasePages";

const num = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v) || 0);
const toArray = (v) => (Array.isArray(v) ? v : v ? String(v).split(",").map((s) => s.trim()).filter(Boolean) : []);

// crews collection (CrewResponseModel) and labors collection
// (CrewLaborResponseModel); a labour belongs to the crew whose Code is in
// its Crew field (LaborDatabaseViewModel.BuildTree).
export const CREW_CONFIG = {
  endpoint: "crews",
  fields: CREW_FIELDS.map((key) => ({ key })),
  fromWire: (w) => {
    const out = { ...w, Awp: toArray(w.Awp) };
    YIELD_FIELDS.forEach((u) => {
      out[`Yield${u}`] = num(w[`Yield${u}`]);
    });
    return out;
  },
  toWire: (rec) => {
    const out = { Code: rec.Code, Description: rec.Description, Awp: toArray(rec.Awp) };
    YIELD_FIELDS.forEach((u) => {
      out[`Yield${u}`] = num(rec[`Yield${u}`]);
    });
    if (rec.isPublic !== undefined) out.isPublic = rec.isPublic;
    return out;
  },
};

export const LABOR_CONFIG = {
  endpoint: "labors",
  fields: LABOR_FIELDS.map((key) => ({ key })),
  fromWire: (w) => ({ ...w, Percentage: num(w.Percentage), UnitCost: num(w.UnitCost) }),
  toWire: (rec) => ({
    Code: rec.Code,
    Description: rec.Description,
    Percentage: num(rec.Percentage),
    UnitCost: num(rec.UnitCost),
    Crew: rec.Crew,
    ...(rec.isPublic !== undefined ? { isPublic: rec.isPublic } : {}),
  }),
};

const norm = (v) => String(v === undefined || v === null ? "" : v).trim().toLowerCase();

export default function LaborDatabasePage() {
  const workspace = useDatabaseWorkspace();
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null); // { kind: 'crew'|'labor', key }
  const [expanded, setExpanded] = useState({});
  const [modal, setModal] = useState(null);
  const [choice, setChoice] = useState(false);
  const [pendingImport, setPendingImport] = useState(null);
  const [workingUnit, setWorkingUnit] = useState("SF");
  const fileRef = useRef(null);

  const crews = useScopedList(workspace, CREW_CONFIG);
  const labors = useScopedList(workspace, LABOR_CONFIG);
  const awps = useLookup(workspace, "awps");

  const metric = isMetricTemplate(workspace.template);
  const rate = workspace.rate || 1;
  const bidlight = workspace.scope === SCOPE_BIDLIGHT;

  // LaborDatabaseViewModel.BuildTree: crew -> labours, crews ordered by
  // Code as text; a labour whose crew is missing borrows the company's crew
  // row, and is not shown when there is none.
  const tree = useMemo(() => {
    const { nodes } = attachChildren(crews.items, labors.items, {
      parentRows: crews.rawRows,
      parentKeyOf: (r) => r.Crew,
      master: workspace.master,
      scope: workspace.scope,
      fromWire: CREW_CONFIG.fromWire,
    });
    return {
      nodes: nodes
        .sort((a, b) => compareCodeText(a.parent.rec.Code, b.parent.rec.Code))
        .map((n) => ({ crew: n.parent, labors: n.children })),
      orphans: [],
    };
  }, [crews.items, labors.items, crews.rawRows, workspace.master, workspace.scope]);

  const visibleNodes = tree.nodes.filter(
    (n) =>
      !search ||
      matchesSearch(n.crew.rec, ["Code", "Description"], search) ||
      n.labors.some((l) => matchesSearch(l.rec, ["Code", "Description"], search))
  );

  const yieldFields = YIELD_FIELDS.map((u) => ({
    key: `Yield${u}`,
    label: `Yield / ${yieldUnitLabel(u, metric)} per hour`,
    type: "number",
    min: 0,
    toInput: (v) => Math.round(yieldToDisplay(u, v, metric) * 10000) / 10000,
    fromInput: (v) => yieldFromDisplay(u, v, metric),
  }));

  const crewFields = [
    { key: "Code", label: "Code", type: "readonly" },
    { key: "Description", label: "Description", type: "text", required: true },
    { key: "Awp", label: "AWP", type: "multiselect", required: true },
    ...yieldFields,
  ];
  const laborFields = [
    { key: "Code", label: "Code", type: "readonly" },
    { key: "Description", label: "Description", type: "text", required: true },
    { key: "Percentage", label: "Percentage (share of a crew hour)", type: "number", min: 0 },
    { key: "UnitCost", label: "Unit Cost per hour", type: "number", currency: true, min: 0 },
  ];

  const activeCrew = () => {
    if (!active) return null;
    if (active.kind === "crew") return crews.items.find((c) => c.key === active.key) || null;
    const l = labors.items.find((x) => x.key === active.key);
    return l ? crews.items.find((c) => String(c.rec.Code) === String(l.rec.Crew)) || null : null;
  };

  const startAddCrew = () =>
    setModal({
      kind: "crew",
      mode: "add",
      initial: { Code: nextTopCode(crews.items.map((c) => c.rec.Code)), Awp: [] },
    });

  const startAddLabor = (crew) => {
    const children = labors.items.filter((l) => String(l.rec.Crew) === String(crew.rec.Code));
    setModal({
      kind: "labor",
      mode: "add",
      initial: {
        Code: nextChildCode(crew.rec.Code, children.map((l) => l.rec.Code)),
        Crew: crew.rec.Code,
        Percentage: 0,
        UnitCost: 0,
      },
    });
  };

  const onAdd = () => {
    if (activeCrew()) setChoice(true);
    else startAddCrew();
  };

  // LaborDatabaseViewModel.CloneItem: a crew is copied with its labours
  // under new codes; a labour is copied into its crew.
  const onClone = () => {
    if (!active) {
      addNotification("There is no item selected to clone.", "danger");
      return;
    }
    if (active.kind === "crew") {
      const crew = crews.items.find((c) => c.key === active.key);
      if (!crew) return;
      const children = labors.items.filter((l) => String(l.rec.Crew) === String(crew.rec.Code)).map((l) => l.rec);
      const copy = cloneParentWithChildren(crew.rec, children, {
        parentCodes: crews.items.map((c) => c.rec.Code),
        parentField: "Crew",
      });
      // CloneItem does not tick the copies.
      crews.add(copy.parent, { selected: false });
      copy.children.forEach((l) => labors.add(l, { selected: false }));
      return;
    }
    const labor = labors.items.find((l) => l.key === active.key);
    if (!labor) return;
    const siblings = labors.items.filter((l) => String(l.rec.Crew) === String(labor.rec.Crew)).map((l) => l.rec.Code);
    labors.add(cloneChild(labor.rec, siblings, { parentField: "Crew" }), { selected: false });
  };

  const onEdit = () => {
    if (!active) {
      addNotification("There is no item selected to edit.", "danger");
      return;
    }
    const list = active.kind === "crew" ? crews : labors;
    const it = list.items.find((x) => x.key === active.key);
    if (it) setModal({ kind: active.kind, mode: "edit", key: it.key, initial: it.rec });
  };

  const onDelete = () => {
    const tickedCrews = crews.items.filter((c) => c.selected).map((c) => c.key);
    const tickedLabors = labors.items.filter((l) => l.selected).map((l) => l.key);
    let crewKeys = tickedCrews;
    let laborKeys = tickedLabors;
    if (!crewKeys.length && !laborKeys.length && active) {
      if (active.kind === "crew") crewKeys = [active.key];
      else laborKeys = [active.key];
    }
    if (!crewKeys.length && !laborKeys.length) {
      addNotification("Select an item first", "danger");
      return;
    }
    // LaborDatabaseViewModel.DeleteItem removes the crew only; its labours
    // stay in the database (and drop out of the tree on the next load).
    const allLabor = [...new Set(laborKeys)];
    confirmDialog({
      title: "Delete",
      content: `Delete ${crewKeys.length} crew(s) and ${allLabor.length} labor row(s)? They are removed from the database when you save.`,
      okText: "Delete",
      onOk: () => {
        if (crewKeys.length) crews.remove(crewKeys);
        if (allLabor.length) labors.remove(allLabor);
        setActive(null);
      },
    });
  };

  const exportData = () => {
    const tickedCrews = crews.items.filter((c) => c.selected);
    const nodes = (tickedCrews.length
      ? tree.nodes.filter((n) => n.crew.selected)
      : visibleNodes
    ).map((n) => ({ crew: n.crew.rec, labors: n.labors.map((l) => l.rec) }));
    downloadWorkbook(buildLaborWorkbook(nodes, { rate, metric }), "LaborData.xlsx");
  };

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = readLaborWorkbook(readWorkbook(new Uint8Array(ev.target.result)), { rate, metric });
        if (!parsed.length) {
          addNotification("There is no data present in the file to import.", "danger");
          return;
        }
        setPendingImport(parsed);
      } catch (err) {
        addNotification("The file could not be read as an Excel workbook.", "danger");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const applyImport = (mode) => {
    const crewRecs = pendingImport.filter((p) => p.crew).map((p) => p.crew);
    const laborRecs = pendingImport.flatMap((p) =>
      p.labors.map((l) => ({ ...l, Crew: p.crew ? p.crew.Code : String(l.Code).split(".")[0] }))
    );
    crews.importRecords(crewRecs, mode, (r) => norm(r.Code));
    labors.importRecords(laborRecs, mode, (r) => norm(r.Code));
    addNotification(
      `${crewRecs.length} crew(s) and ${laborRecs.length} labor row(s) read from the file. Save to write them.`,
      "success"
    );
    setPendingImport(null);
  };

  const beforeSave = () => {
    const bad = crews.items.find((c) => (c.status !== "clean" || c.selected) && !toArray(c.rec.Awp).length);
    if (bad && !bidlight) return `Crew ${bad.rec.Code} needs at least one AWP.`;
    return null;
  };

  const awpOpts = awpOptions(awps);
  const isActive = (kind, key) => active && active.kind === kind && active.key === key;

  const laborRow = (l, indent) => (
    <tr
      key={l.key}
      className={isActive("labor", l.key) ? "table-active" : ""}
      onClick={() => setActive({ kind: "labor", key: l.key })}
      onDoubleClick={() => workspace.canEdit && setModal({ kind: "labor", mode: "edit", key: l.key, initial: l.rec })}
      style={{ cursor: "pointer" }}
      data-testid="labor-row"
    >
      <td onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          className="squared-checkbox"
          checked={!!l.selected}
          onChange={(e) => {
            // Ticking a labour ticks its crew (LaborDatabaseViewModel).
            labors.select([l.key], e.target.checked);
            if (e.target.checked) {
              const parent = crews.items.find((c) => String(c.rec.Code) === String(l.rec.Crew));
              if (parent) crews.select([parent.key], true);
            }
          }}
        />
      </td>
      <td className="f-14" style={{ paddingLeft: indent }}>
        {l.rec.Code}
      </td>
      <td className="f-14">{l.rec.Description}</td>
      <td className="f-14">{formatNumber(l.rec.Percentage)}</td>
      <td className="f-14">{formatMoney(l.rec.UnitCost, rate)}</td>
      <td colSpan={4} />
      <td className="f-12 text-muted">{!bidlight && l.status !== "clean" ? (l.status === "new" ? "New" : "Edited") : ""}</td>
    </tr>
  );

  const unitLabel = yieldUnitLabel(workingUnit, metric);

  return (
    <DatabasePageShell
      title="Labor & Crews"
      workspace={workspace}
      lists={[crews, labors]}
      searchValue={search}
      setSearchValue={setSearch}
      onAdd={onAdd}
      onEdit={onEdit}
      onDelete={onDelete}
      onClone={onClone}
      onExport={exportData}
      onImport={() => fileRef.current && fileRef.current.click()}
      beforeSave={beforeSave}
      addLabel="Add crew or labor"
    >
      <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls" style={{ display: "none" }} onChange={onFile} />
      <div className="d-flex align-items-center gap-2 mb-2 f-14">
        <span>Show yield and cost per</span>
        <select
          className="form-control form-control-sm"
          style={{ width: 100 }}
          value={workingUnit}
          onChange={(e) => setWorkingUnit(e.target.value)}
        >
          {YIELD_FIELDS.map((u) => (
            <option key={u} value={u}>
              {yieldUnitLabel(u, metric)}
            </option>
          ))}
        </select>
      </div>
      <div className="table-responsive" style={{ maxHeight: "calc(100 * var(--vh) - 360px)", overflow: "auto" }}>
        <table className="table table-sm table-hover mb-0" data-testid="labor-table">
          <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
            <tr>
              <th style={{ width: 36 }} />
              <th className="f-14">Code</th>
              <th className="f-14">Description</th>
              <th className="f-14">Percentage</th>
              <th className="f-14">Unit Cost / hr</th>
              <th className="f-14">AWP</th>
              <th className="f-14">Yield / {unitLabel} per hr</th>
              <th className="f-14">Crew cost / hr</th>
              <th className="f-14">Cost / {unitLabel}</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {visibleNodes.length === 0 && tree.orphans.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center text-muted py-4">
                  {!bidlight && crews.firstLoad
                    ? "The company has no crews for this version yet. Switch to the BidLight database, tick the crews you want and save to copy them."
                    : "No crews for this version."}
                </td>
              </tr>
            )}
            {visibleNodes.map(({ crew, labors: children }) => {
              const open = expanded[crew.key] || !!search;
              const recs = children.map((l) => l.rec);
              const total = crewTotalPercentage(recs);
              return (
                <React.Fragment key={crew.key}>
                  <tr
                    className={isActive("crew", crew.key) ? "table-active" : ""}
                    onClick={() => setActive({ kind: "crew", key: crew.key })}
                    onDoubleClick={() =>
                      workspace.canEdit && setModal({ kind: "crew", mode: "edit", key: crew.key, initial: crew.rec })
                    }
                    style={{ cursor: "pointer", fontWeight: 600 }}
                    data-testid="crew-row"
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="squared-checkbox"
                        checked={!!crew.selected}
                        onChange={(e) => {
                          crews.select([crew.key], e.target.checked);
                          labors.select(children.map((l) => l.key), e.target.checked);
                        }}
                      />
                    </td>
                    <td className="f-14">
                      <span
                        className="mr-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpanded((x) => ({ ...x, [crew.key]: !open }));
                        }}
                      >
                        {children.length ? (open ? "▾" : "▸") : "  "}
                      </span>
                      {crew.rec.Code}
                    </td>
                    <td className="f-14">{crew.rec.Description}</td>
                    <td className="f-14" title="Total of the labor percentages">
                      {formatNumber(total)}
                    </td>
                    <td />
                    <td className="f-14">{toArray(crew.rec.Awp).join(", ") || "--"}</td>
                    <td className="f-14">{formatNumber(yieldForUnit(crew.rec, metric ? unitLabel : workingUnit))}</td>
                    <td className="f-14">{formatMoney(crewHourlyCost(recs), rate)}</td>
                    <td className="f-14">
                      {formatMoney(crewCostPerUnit(crew.rec, recs, metric ? unitLabel : workingUnit), rate)}
                    </td>
                    <td className="f-12 text-muted">
                      {!bidlight && crew.status !== "clean" ? (crew.status === "new" ? "New" : "Edited") : ""}
                    </td>
                  </tr>
                  {open && children.map((l) => laborRow(l, 28))}
                </React.Fragment>
              );
            })}
            {tree.orphans.length > 0 && (
              <>
                <tr>
                  <td colSpan={10} className="f-12 text-muted">
                    Labor rows whose crew is not in this version
                  </td>
                </tr>
                {tree.orphans.map((l) => laborRow(l, 12))}
              </>
            )}
          </tbody>
        </table>
      </div>

      <ChoiceModal
        open={choice}
        title="Which item do you want to add?"
        choices={[
          { value: "crew", label: "Crew" },
          { value: "labor", label: "Labor" },
        ]}
        onCancel={() => setChoice(false)}
        onChoose={(v) => {
          setChoice(false);
          if (v === "crew") startAddCrew();
          else startAddLabor(activeCrew());
        }}
      />
      <RecordModal
        open={!!modal}
        title={modal ? `${modal.mode === "edit" ? "Edit" : "Add"} ${modal.kind === "crew" ? "crew" : "labor"}` : ""}
        fields={modal && modal.kind === "crew" ? crewFields : laborFields}
        initial={modal ? modal.initial : null}
        rate={rate}
        options={(f) => (f.key === "Awp" ? awpOpts : [])}
        onCancel={() => setModal(null)}
        onSubmit={(rec) => {
          const list = modal.kind === "crew" ? crews : labors;
          if (modal.mode === "edit") list.edit(modal.key, rec);
          else list.add({ ...rec, isPublic: false });
          if (modal.kind === "labor" && modal.mode === "add") {
            const parent = crews.items.find((c) => String(c.rec.Code) === String(rec.Crew));
            if (parent) setExpanded((x) => ({ ...x, [parent.key]: true }));
          }
          setModal(null);
        }}
      />
      <ImportModeModal open={!!pendingImport} onCancel={() => setPendingImport(null)} onConfirm={applyImport} />
    </DatabasePageShell>
  );
}
